import { FC, useEffect, useState } from "react";
import Song from "../models/song";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDuration, formatNumber } from "../utils/formatUtils";
import useSongPlayer from "../hooks/useSongPlayer";
import posthog from "posthog-js";

type Props = {
	song: Song;
};

const SongPlayerCompact: FC<Props> = ({ song }) => {
	const songPlayer = useSongPlayer();

	const [isThisSongPlaying, setIsThisSongPlaying] = useState(false);

	useEffect(() => {
		if (songPlayer.song && songPlayer.song.id === song.id) {
			setIsThisSongPlaying(songPlayer.isPlaying);
		} else {
			setIsThisSongPlaying(false);
		}
	}, [songPlayer.song, song.id, songPlayer.isPlaying]);

	const playPause = () => {
		if (isThisSongPlaying) {
			songPlayer.pause();
		} else {
			if (songPlayer.song && songPlayer.song.id === song.id) {
				songPlayer.resume();
			} else {
				songPlayer.play(song);

				posthog.capture('song_played', {
					songId: song.id,
					songTitle: song.title,
					songAlbum: song.album?.name || '',
					origin: 'song_dialog'
				});
			}
		}
	};

	return (
		<Paper elevation={12} sx={{ p: 2 }}>
			<Stack direction="row" spacing={2}>
				<Box>
					<IconButton size="large" sx={{ width: 52 }} className="player-button" onClick={playPause}>
						<FontAwesomeIcon icon={{ prefix: 'fas', iconName: isThisSongPlaying ? 'pause' : 'play' }} style={{ transform: isThisSongPlaying ? 'translateY(1px)' : 'translate(2px, 1px)' }} />
					</IconButton>
				</Box>
				<Stack>
					<Typography sx={{ typography: { xs: 'body2', sm: 'body2', md: 'body1', lg: 'h6', xl: "h6" } }}>{song.title}</Typography>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', sm: 'none', md: 'flex', lg: 'flex', xl: 'flex' }, color: 'GrayText' }}>
						{song.album && song.album.name && (
							<>
								<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body2', xl: "body2" } }}>{song.album.name}</Typography>
								<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'circle' }} style={{ fontSize: 6 }} />
							</>
						)}
						{song.beatsPerMinute && (
							<>
								<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body2', xl: "body2" } }} style={{ fontFeatureSettings: `'tnum'` }}>{formatNumber(+song.beatsPerMinute)} BPM</Typography>
								<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'circle' }} style={{ fontSize: 6 }} />
							</>
						)}
						{song.duration && (
							<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body2', xl: "body2" } }} style={{ fontFeatureSettings: `'tnum'` }}>{formatDuration(song.duration)}</Typography>
						)}
					</Stack>
					<Stack sx={{ display: { xs: 'flex', sm: 'flex', md: 'none', lg: 'none', xl: 'none' }, color: 'GrayText' }}>
						{song.album && song.album.name && (
							<Box>
								<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body1', xl: "body1" } }}>{song.album.name}</Typography>
							</Box>
						)}
						{(song.beatsPerMinute || song.duration) && (
							<Stack direction="row" spacing={1} alignItems="center">
								{song.beatsPerMinute && (
									<>
										<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body2', xl: "body2" } }} style={{ fontFeatureSettings: `'tnum'` }}>{formatNumber(+song.beatsPerMinute)} BPM</Typography>
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'circle' }} style={{ fontSize: 6 }} />
									</>
								)}
								{song.duration && (
									<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body2', lg: 'body2', xl: "body2" } }} style={{ fontFeatureSettings: `'tnum'` }}>{formatDuration(song.duration)}</Typography>
								)}
							</Stack>
						)}
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
};

export default SongPlayerCompact;
